import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

// Material UI Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';

import { useParameter } from '../../../context/ParameterContext';
import ParameterForm from '../../../forms/ParameterForm';
import { Parameter } from '../../../models/Parameter';
import Button from '../../../components/formElements/Button';
import Loading from '../../../components/global/loading/Loading';

function EditParameter({ id }: { id: string }) {
  const { getParameter } = useParameter();
  const [parameter, setParameter] = React.useState<Parameter | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchParameter() {
      if (id) {
        setIsLoading(true);
        try {
          const parameter = await getParameter(parseInt(id));
          setParameter(parameter);
        } catch (error: any) {
          toast.error('Parametr nie istnieje');
          navigate('/admin/parametry');
        } finally {
          setIsLoading(false);
        }
      }
    }

    fetchParameter();
  }, [id, getParameter, navigate]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Nagłówek z przyciskiem powrotu */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                navigate(
                  parameter?.categoryId
                    ? `/admin/parametry/${parameter.categoryId}`
                    : '/admin/parametry',
                );
              }}
              colorTheme="outline"
              className="w-auto px-3 py-1.5"
            >
              <ArrowBackIcon fontSize="small" className="mr-1" />
              Powrót
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              Edycja parametru
            </h1>
          </div>
          {parameter && (
            <div className="mt-2 flex items-center gap-2">
              <div className="bg-primary/10 px-3 py-1.5 rounded-md text-primary font-medium">
                {parameter.name}
              </div>

              {parameter.category && (
                <div className="bg-gray-50 px-3 py-1.5 rounded-md border border-gray-100 flex items-center gap-1">
                  <CategoryIcon className="text-gray-500" fontSize="small" />
                  <span>
                    {parameter.categoryId
                      ? parameter.category.name
                      : 'Parametr globalny'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Formularz parametru */}
      {parameter && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <ParameterForm
            categoryId={parameter.categoryId || undefined}
            id={parameter.id}
            name={parameter.name}
            description={parameter.description || ''}
            inEditMode={true}
          />
        </div>
      )}
    </motion.div>
  );
}

export default EditParameter;
